import type { FC } from "react";

import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import { useSelector } from "@jti/store/reduxHooks";
import { Status } from "@jti/store/slices/loyaltyCoreSlice";
import { type RootStoreState } from "@jti/store/types";
import { DEPRECATED_Loading } from "@jti/ui/components/DEPRECATED_Loading";
import { Tooltip } from "@jti/ui/components/Tooltip";
import { Typography } from "@jti/ui/components/Typography";
import { formatDate } from "@jti/ui/utils/formatDate";

import useLoggedUser from "../../hooks/useLoggedUser";
import { timeToDate } from "../../utils/timeToDate";

import styles from "./PointsAmountBanner.module.scss";

export type PointsAmountBannerProps = {
  pointsSingular: string;
  pointsPlural: string;
  text: string;
  tooltipText: string;
  url: string;
  urlText: string;
  hoursText?: string;
  minutesText?: string;
  daysSingularText?: string;
  daysPluralText?: string;
  weeksSingularText?: string;
  weeksPluralText?: string;
  componentId?: string;
  isMarginRemoved?: boolean;
  showDate?: boolean;
};

export const PointsAmountBanner: FC<PointsAmountBannerProps> = ({
  pointsSingular,
  pointsPlural,
  text,
  tooltipText,
  url,
  urlText,
  hoursText,
  minutesText,
  daysSingularText,
  daysPluralText,
  weeksSingularText,
  weeksPluralText,
  componentId,
  isMarginRemoved,
  showDate,
}) => {
  const [isLogged] = useLoggedUser();
  const { balance, points_to_expire, expire_at, status } = useSelector(
    (state: RootStoreState) => state.loyaltyCore.points
  );
  const hasExpirationDate = expire_at && expire_at !== "0";
  const shouldUseFullBalance = !hasExpirationDate;
  const displayedPoints = shouldUseFullBalance ? balance : points_to_expire;
  const pointsText = displayedPoints === 1 ? pointsSingular : pointsPlural;
  const shouldShowExpirationDetails = balance > 0 && hasExpirationDate && displayedPoints > 0;

  const expirationValue = showDate
    ? formatDate(expire_at)
    : timeToDate(
        expire_at,
        hoursText,
        minutesText,
        daysSingularText,
        daysPluralText,
        weeksSingularText,
        weeksPluralText
      );

  if (!isLogged) {
    return null;
  }

  return (
    <div
      className={classNames(styles.accountPointsAmountBanner, {
        [styles.noMargin]: isMarginRemoved,
      })}
    >
      {status === Status.COMPLETED ? (
        <Typography variant="body2" weight="light" dataTestId={componentId} className={styles.colorGrey}>
          <span>
            <b>{String(displayedPoints)}</b> {pointsText}{" "}
            {shouldShowExpirationDetails && (
              <>
                {text} <b>{expirationValue}</b>
              </>
            )}{" "}
          </span>
        </Typography>
      ) : (
        <DEPRECATED_Loading />
      )}
      <div className={styles.accountPointsAmountBannerRightSide}>
        <Tooltip
          content={
            <>
              <div>{tooltipText}</div>
              <Button variant="text" href={url}>
                {urlText}
              </Button>
            </>
          }
        />
      </div>
    </div>
  );
};
